import { useState } from "react"
import {
  BookOpen, LogIn, LayoutGrid, FileText, TestTube, PlusCircle, Paperclip,
  CheckCircle2, XCircle, Building2, Users, ShieldCheck, HelpCircle, Search,
  Printer, ImageOff, AlertTriangle, PlayCircle, Bot, Lock,
} from "lucide-react"
import { Input } from "./ui/input"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Alert, AlertDescription } from "./ui/alert"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { useUserRol, useTienePermiso } from "../App"
import { ROL_LABELS } from "../lib/permisos"

function Captura({ src, alt, nota }: { src?: string; alt: string; nota?: string }) {
  if (!src) {
    return (
      <div className="my-3 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-8 text-muted-foreground">
        <ImageOff className="h-5 w-5" />
        <span className="text-xs">Captura pendiente de agregar</span>
      </div>
    )
  }
  return (
    <figure className="my-3 overflow-hidden rounded-lg border">
      <img src={src} alt={alt} className="w-full" loading="lazy" />
      {nota && (
        <figcaption className="border-t bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          {nota}
        </figcaption>
      )}
    </figure>
  )
}

function Paso({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
        {n}
      </span>
      <span className="text-muted-foreground">{children}</span>
    </li>
  )
}

interface Seccion {
  id: string
  titulo: string
  icon: React.ComponentType<{ className?: string }>
  keywords: string
  adminOnly?: boolean
  contenido: React.ReactNode
}

interface Limitacion {
  id: string
  titulo: string
  descripcion: string
  justificacion: string
  riesgo: string
  control: string
}

// Borrador inicial para el expediente de validación (ISO/IEC 17025 §7.11.2 + enfoque de
// riesgo GAMP 5). Pendiente de revisión y ampliación por el responsable de calidad.
const limitacionesSistema: Limitacion[] = [
  {
    id: "LIM-01",
    titulo: "Aislamiento de datos de Microsip",
    descripcion: "Los registros importados o sincronizados desde Microsip se almacenan de forma aislada, sin vincularse a los datos de las tarjetas de control del laboratorio.",
    justificacion: "Evitar mezclar el sistema contable/comercial (Microsip) con los datos técnicos del laboratorio.",
    riesgo: "Pérdida de integridad o trazabilidad de los resultados si se combinan datos de distinta naturaleza.",
    control: "Separación a nivel de tabla/esquema, sin relaciones (llaves foráneas) entre ambos conjuntos de datos.",
  },
  {
    id: "LIM-02",
    titulo: "Documentos de soporte no se interpretan",
    descripcion: "El sistema únicamente almacena el archivo adjunto como respaldo; no abre, valida ni extrae su contenido (PDF, Word .doc/.docx, imágenes).",
    justificacion: "El adjunto es evidencia de respaldo, no una fuente de datos que el sistema deba procesar.",
    riesgo: "Un archivo incorrecto, corrupto o ilegible puede confirmarse sin que el sistema lo detecte.",
    control: "El usuario debe revisar el archivo antes de confirmar la subida; el sistema advierte que la acción es irreversible.",
  },
  {
    id: "LIM-03",
    titulo: "Formato exclusivo para la nota del reporte al cliente",
    descripcion: "Para la nota que se incorpora al reporte del cliente solo se acepta .docx (Word 2007 en adelante); el formato antiguo .doc (Word 97-2003) no se procesa.",
    justificacion: "El motor de lectura de contenido usado para transcribir el texto no soporta el formato binario antiguo.",
    riesgo: "Un archivo .doc subido no se leería correctamente y el reporte quedaría incompleto.",
    control: "El sistema rechaza cualquier archivo que no sea .docx antes de aceptarlo.",
  },
  {
    id: "LIM-04",
    titulo: "Documentos adjuntos no editables tras confirmarse",
    descripcion: "Un documento de soporte, una vez confirmado, no puede modificarse ni eliminarse desde el sistema.",
    justificacion: "Preservar la integridad de la evidencia registrada; evita el reemplazo silencioso de un archivo ya ligado a una prueba.",
    riesgo: "Un archivo erróneo permanece ligado al pedido si no se revisó antes de confirmar.",
    control: "Confirmación explícita previa a la subida; la corrección requiere intervención directa de un Administrador fuera del flujo estándar.",
  },
  {
    id: "LIM-05",
    titulo: "Formato restringido para firma/sello del informe",
    descripcion: "Solo se aceptan imágenes JPG o PNG para la firma/sello al cerrar un pedido y generar el informe.",
    justificacion: "Simplificar el renderizado del documento final con formatos que el generador de reportes soporta de forma confiable.",
    riesgo: "Otros formatos (PDF, GIF, WEBP, HEIC, etc.) no pueden usarse directamente en el informe.",
    control: "El sistema rechaza cualquier formato distinto a JPG/PNG al momento de subir la imagen.",
  },
  {
    id: "LIM-06",
    titulo: "Un pedido cerrado no se edita: se reabre como reemisión",
    descripcion: "Un pedido cerrado no puede editarse directamente. Reabrirlo genera una reemisión: un pedido nuevo enlazado al original (si se aporta evidencia que sustente la reapertura), o una solicitud que debe aprobar explícitamente una persona con autorización específica para reaperturas (no depende solo del rol).",
    justificacion: "Evitar alteraciones no controladas a resultados que ya fueron autorizados y, en su caso, entregados al cliente.",
    riesgo: "Modificación no trazable de datos ya emitidos si cualquier usuario pudiera reabrir y editar un pedido cerrado libremente.",
    control: "Toda reapertura queda registrada de forma permanente (quién la solicitó, quién la autorizó cuando aplica, cuándo y con qué evidencia); el pedido original se conserva sin tocarse y la reapertura se refleja como un pedido nuevo, nunca como una edición silenciosa del cerrado.",
  },
  {
    id: "LIM-07",
    titulo: "Acceso limitado a cuentas dadas de alta",
    descripcion: "Solo pueden iniciar sesión correos de Google que estén registrados previamente en la sección Usuarios.",
    justificacion: "Controlar qué personas tienen acceso al sistema y con qué rol, sin depender de contraseñas propias del sistema.",
    riesgo: "Acceso no autorizado si no se restringiera a una lista controlada de usuarios.",
    control: "Autenticación federada con Google combinada con una lista blanca de usuarios administrada por un Administrador.",
  },
  {
    id: "LIM-08",
    titulo: "Sincronización periódica con Microsip (no en tiempo real)",
    descripcion: "La información base del pedido (según lo operado por el laboratorio) se recibe desde Microsip mediante una sincronización periódica, aproximadamente cada 15 minutos, no en tiempo real.",
    justificacion: "Es un proceso de sincronización por lotes entre dos sistemas independientes, no una integración instantánea.",
    riesgo: "Un cambio hecho en Microsip puede tardar hasta el siguiente ciclo de sincronización en reflejarse en Dexterite.",
    control: "La sincronización corre de forma automática y periódica sin intervención manual. (Confirmar y documentar formalmente el mecanismo exacto con el área de TI o el proveedor de la integración antes de incluir esta fila en el expediente final.)",
  },
  {
    id: "LIM-09",
    titulo: "Una prueba aceptada no se edita por el flujo normal",
    descripcion: "Una vez que una prueba queda en estado ACEPTADO, ni el Analista ni el Coordinador pueden modificarla desde el flujo estándar. La única vía de corrección es que un Administrador la edite explícitamente con la opción \"Editar\".",
    justificacion: "Preservar la integridad de un resultado ya revisado, permitiendo al mismo tiempo corregir errores de forma controlada en vez de prohibirlo por completo.",
    riesgo: "Si la edición posterior a la aceptación no quedara marcada, un resultado ya aceptado podría alterarse sin que nadie lo note.",
    control: "Toda edición posterior a la aceptación se marca visualmente (ícono de \"editado\") y queda registrada en un historial de ediciones consultable, con quién y cuándo hizo el cambio.",
  },
  {
    id: "LIM-10",
    titulo: "El Reporte Cliente puede omitir pruebas; la Bitácora nunca",
    descripcion: "Cada prueba tiene un interruptor \"Reporte cliente\" que decide si aparece o no en el informe que recibe el cliente. La Bitácora interna, en cambio, siempre incluye el 100% de las pruebas del pedido, sin excepción — el interruptor nunca la afecta.",
    justificacion: "Permitir controlar qué resultados se comparten formalmente con el cliente sin perder ningún dato en el registro interno de trazabilidad.",
    riesgo: "El Reporte Cliente y la Bitácora pueden no coincidir en número de pruebas si no se entiende esta diferencia al revisar un expediente.",
    control: "El interruptor se bloquea (ya no se puede cambiar) en cuanto el pedido se cierra, quedando fijo el estado con el que se generó el reporte final.",
  },
]

export function DocumentacionView() {
  const [busqueda, setBusqueda] = useState("")
  const rol = useUserRol()
  const esAdmin = rol === 'ADMIN'
  const puedeVerContactos = useTienePermiso('ver_contactos')
  const puedeAdministrarCatalogo = useTienePermiso('administrar_catalogo')
  const puedeCapturarResultado = useTienePermiso('capturar_resultado')
  const puedeEditarBorrador = useTienePermiso('editar_resultado_borrador')
  const puedeVerBitacoras = useTienePermiso('ver_bitacoras')

  const secciones: Seccion[] = [
    {
      id: "intro",
      titulo: "¿Qué es Dexterite?",
      icon: BookOpen,
      keywords: "introducción roles analista coordinador gerente calidad administrador",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Dexterite es el sistema donde el laboratorio captura, revisa y cierra las pruebas de cada
            pedido de trabajo, y genera la bitácora interna y el reporte para el cliente. Todo lo que
            antes se hacía en papel u hojas sueltas se llena aquí, y queda disponible para todo el
            equipo.
          </p>
          <p className="text-muted-foreground">
            Lo que puedes hacer dentro del sistema depende de tu <strong>rol</strong>. Los roles
            existentes son:
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Object.entries(ROL_LABELS).map(([clave, etiqueta]) => (
              <div key={clave} className="rounded-md border px-3 py-2">
                <Badge variant={clave === 'ADMIN' ? 'default' : 'outline'} className="mb-1">
                  {etiqueta}
                </Badge>
                <p className="text-xs text-muted-foreground">
                  {clave === 'ADMIN' && 'Acceso completo a todo el sistema, siempre.'}
                  {clave === 'ANALISTA' && 'Captura y edita resultados de prueba en borrador.'}
                  {clave === 'COORDINADOR' && 'Captura resultados y además puede aprobar/rechazar pruebas.'}
                  {clave === 'GERENTE' && 'Autoriza informes, cierra y reabre pedidos.'}
                </p>
              </div>
            ))}
          </div>
          <Alert>
            <AlertDescription>
              Lo que ves en tu barra lateral y en este manual está ajustado a tu rol. Si crees que te
              falta acceso a algo, pídele a un Administrador que lo revise en la sección{" "}
              <strong>Permisos</strong>.
            </AlertDescription>
          </Alert>
        </div>
      ),
    },
    {
      id: "limitaciones",
      titulo: "Alcance y limitaciones del sistema",
      icon: AlertTriangle,
      keywords: "limitaciones alcance validación gamp ema acreditación riesgo microsip restricciones",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Además de los roles y permisos, el sistema opera bajo restricciones de diseño
            deliberadas y bajo un flujo de trazabilidad fijo. Documentarlos responde al numeral
            7.11.2 de la norma de acreditación: la validación del sistema de gestión de la
            información del laboratorio debe dejar constancia de su funcionalidad, incluyendo los
            límites bajo los que opera y cómo queda registrado cada cambio.
          </p>
          <Alert>
            <AlertDescription>
              Borrador inicial para el expediente de validación. Debe revisarlo y completarlo el
              responsable de calidad antes de incorporarlo como documento formal ante la EMA.
            </AlertDescription>
          </Alert>

          <h4 className="font-medium">Trazabilidad del ciclo de vida de una prueba</h4>
          <p className="text-sm text-muted-foreground">
            De principio a fin, una prueba pasa por los siguientes pasos. En cada uno queda un
            registro de quién hizo qué y cuándo — no hay pasos que se puedan saltar ni deshacer
            sin dejar huella.
          </p>
          <ol className="space-y-3">
            <Paso n={1}>
              <strong>Ingreso de datos.</strong> La información base del pedido llega desde
              Microsip mediante una sincronización periódica (ver <strong>LIM-08</strong> en la
              tabla de abajo). Dexterite no modifica ni valida ese dato de origen: solo lo recibe
              y lo mantiene separado de los datos técnicos propios del laboratorio (ver{" "}
              <strong>LIM-01</strong>).
            </Paso>
            <Paso n={2}>
              <strong>Captura.</strong> El Analista asignado llena la prueba con los datos
              técnicos necesarios (ver "Llenar una prueba"). Mientras no se guarde como definitiva
              queda como <strong>BORRADOR</strong>, recuperable con <strong>CONTINUAR</strong>.
            </Paso>
            <Paso n={3}>
              <strong>Revisión.</strong> Un Coordinador, Gerente de Calidad o Administrador revisa
              lo capturado y decide: <strong>ACEPTAR</strong>, <strong>RECHAZAR</strong> (con
              motivo obligatorio, visible para quien capturó) o corregir directamente si detecta un
              error (ver paso 4).
            </Paso>
            <Paso n={4}>
              <strong>Prueba aceptada = cerrada al flujo normal.</strong> Una vez en estado{" "}
              <strong>ACEPTADO</strong>, ni el Analista ni el Coordinador pueden modificarla desde
              el flujo estándar. La única corrección posible es que un{" "}
              <strong>Administrador</strong> la edite explícitamente, y esa edición siempre queda
              marcada y registrada en un historial de ediciones consultable (ver{" "}
              <strong>LIM-09</strong>).
            </Paso>
            <Paso n={5}>
              <strong>Reporte Cliente vs. Bitácora.</strong> Cada prueba tiene un interruptor para
              incluirla o no en el Reporte Cliente. La Bitácora interna siempre incluye el 100% de
              las pruebas del pedido, sin excepción — el interruptor nunca la afecta (ver{" "}
              <strong>LIM-10</strong>).
            </Paso>
            <Paso n={6}>
              <strong>Cierre del pedido.</strong> Al cerrar, el sistema genera la Bitácora y el
              Reporte Cliente definitivos y bloquea el pedido completo: ya no se pueden agregar,
              aceptar, rechazar ni editar pruebas, ni tocar el interruptor de Reporte Cliente.
            </Paso>
            <Paso n={7}>
              <strong>Reapertura = reemisión, nunca edición silenciosa.</strong> Un pedido cerrado
              no se reabre para editarse en el mismo registro: se genera una reemisión (pedido
              nuevo enlazado al original) con evidencia que la sustente, o una solicitud que debe
              aprobar explícitamente una persona con autorización específica para reaperturas.
              Quién solicitó, quién autorizó, cuándo y con qué evidencia queda guardado de forma
              permanente (ver <strong>LIM-06</strong>).
            </Paso>
          </ol>

          <h4 className="font-medium">Registro de limitaciones y controles</h4>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">ID</TableHead>
                  <TableHead>Limitación</TableHead>
                  <TableHead>Justificación</TableHead>
                  <TableHead>Riesgo si no se controla</TableHead>
                  <TableHead>Control mitigante</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {limitacionesSistema.map((lim) => (
                  <TableRow key={lim.id} className="align-top">
                    <TableCell className="whitespace-nowrap font-mono text-xs">{lim.id}</TableCell>
                    <TableCell className="min-w-[220px] whitespace-normal">
                      <p className="font-medium">{lim.titulo}</p>
                      <p className="text-xs text-muted-foreground">{lim.descripcion}</p>
                    </TableCell>
                    <TableCell className="min-w-[200px] whitespace-normal text-muted-foreground">{lim.justificacion}</TableCell>
                    <TableCell className="min-w-[200px] whitespace-normal text-muted-foreground">{lim.riesgo}</TableCell>
                    <TableCell className="min-w-[200px] whitespace-normal text-muted-foreground">{lim.control}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ),
    },
    {
      id: "login",
      titulo: "Iniciar sesión",
      icon: LogIn,
      keywords: "login google correo acceso autorizado",
      contenido: (
        <div className="space-y-3">
          <ol className="space-y-2">
            <Paso n={1}>Entra a la dirección del sistema y presiona <strong>Continuar con Google</strong>.</Paso>
            <Paso n={2}>Elige tu cuenta de Google (la misma con la que te dieron de alta en el sistema).</Paso>
            <Paso n={3}>
              Si tu correo no está registrado en <strong>Usuarios</strong>, verás el mensaje
              "Tu email no está autorizado para acceder." — pide a un Administrador que te agregue.
            </Paso>
          </ol>
        </div>
      ),
    },
    {
      id: "navegacion",
      titulo: "Navegación general",
      icon: LayoutGrid,
      keywords: "sidebar menu barra lateral secciones",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            La barra lateral izquierda es el punto de partida. Las secciones que ves ahí (Pedidos,
            Lista de Pruebas, Clientes, Usuarios, Permisos, Documentación) cambian según tu rol —
            por ejemplo, solo un Administrador ve "Usuarios" y "Permisos".
          </p>
          <Captura src="/docs/manual/pedidos-lista.jpg" alt="Pantalla principal de Pedidos con la barra lateral" nota="Pantalla principal: Pedidos, con la barra lateral a la izquierda." />
        </div>
      ),
    },
    {
      id: "pedidos-lista",
      titulo: "Ver y buscar pedidos",
      icon: FileText,
      keywords: "pedidos lista buscar filtro fecha estado abierto cerrado",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            "Pedidos" es la pantalla donde entras al abrir el sistema. Cada tarjeta es una orden de
            trabajo, con su número, estado (abierto/cerrado) y cuántas pruebas tiene.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>Usa el buscador para encontrar un pedido por número.</li>
            <li>El filtro de <strong>Fecha de emisión</strong> acota por rango de fechas.</li>
            <li>El menú <strong>Todos / Abierto / Cerrado / Reportes</strong> filtra por estado del pedido.</li>
            <li>Haz clic en cualquier tarjeta para abrir el detalle del pedido.</li>
          </ul>
          <Captura src="/docs/manual/pedidos-lista.jpg" alt="Lista de pedidos con filtros" />
        </div>
      ),
    },
    {
      id: "pedido-detalle",
      titulo: "Detalle de un pedido y sus pruebas",
      icon: FileText,
      keywords: "detalle pedido pruebas solicitadas borrador sin revisión aceptado rechazado continuar llenar",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Al abrir un pedido ves su información general (artículos, fechas, cantidad) y la lista de
            <strong> PRUEBAS SOLICITADAS</strong>. Cada prueba muestra una etiqueta de estado:
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">SIN REVISIÓN</Badge>
            <Badge variant="outline">BORRADOR</Badge>
            <Badge style={{ backgroundColor: '#059669', color: '#fff' }}>ACEPTADO</Badge>
            <Badge variant="destructive">RECHAZADO</Badge>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li><strong>LLENAR</strong> — la prueba no se ha capturado; abre el formulario en blanco.</li>
            <li><strong>CONTINUAR</strong> — ya hay datos guardados en borrador; continúa donde quedaste.</li>
            <li>El ícono de clip/subir junto a cada prueba adjunta un documento de soporte.</li>
            <li><strong>Vista previa Cliente</strong> / <strong>Vista previa Bitácora</strong> muestran cómo se vería cada reporte con lo capturado hasta el momento.</li>
          </ul>
          <Captura src="/docs/manual/pedido-detalle-sin-revision.jpg" alt="Detalle de un pedido con una prueba sin revisión" />
        </div>
      ),
    },
    {
      id: "llenar-prueba",
      titulo: "Llenar una prueba",
      icon: TestTube,
      keywords: "llenar formulario capturar resultado guardar",
      contenido: (
        <div className="space-y-3">
          <ol className="space-y-2">
            <Paso n={1}>Desde el detalle del pedido, presiona <strong>LLENAR</strong> (o <strong>CONTINUAR</strong> si ya hay un borrador).</Paso>
            <Paso n={2}>Completa los campos del formulario. Cada norma tiene su propio formulario, pero casi todos piden fecha, quién realizó y quién supervisó la prueba.</Paso>
            <Paso n={3}>Presiona <strong>Guardar Formulario</strong>. Se guarda como borrador y puedes retomarlo después con <strong>CONTINUAR</strong>.</Paso>
            <Paso n={4}><strong>Cancelar</strong> descarta los cambios que no hayas guardado y regresa al detalle del pedido.</Paso>
          </ol>
          <Captura src="/docs/manual/formulario-prueba-vacio.jpg" alt="Formulario de análisis en blanco" />
        </div>
      ),
    },
    {
      id: "agregar-prueba",
      titulo: "Agregar una prueba manualmente",
      icon: PlusCircle,
      keywords: "agregar prueba seleccionar catálogo físicas químicas",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Si un pedido necesita una prueba que no vino precargada de los artículos, agrégala a mano:
          </p>
          <ol className="space-y-2">
            <Paso n={1}>En el detalle del pedido, presiona <strong>AGREGAR</strong> (debajo de la lista de pruebas).</Paso>
            <Paso n={2}>Busca la prueba por nombre, o filtra por <strong>Físicas</strong> / <strong>Químicas</strong>.</Paso>
            <Paso n={3}>Presiona <strong>Seleccionar Prueba</strong> en la que necesitas — se agrega a la lista del pedido lista para llenarse.</Paso>
          </ol>
          <Captura src="/docs/manual/modal-seleccionar-prueba.jpg" alt="Modal para seleccionar una prueba a agregar" />
        </div>
      ),
    },
    {
      id: "adjuntar-doc",
      titulo: "Adjuntar un documento de soporte",
      icon: Paperclip,
      keywords: "adjuntar documento subir pdf word doc docx imagen jpg png formatos permitidos",
      contenido: (
        <div className="space-y-3">
          <ol className="space-y-2">
            <Paso n={1}>En la fila de la prueba, presiona el ícono de subir documento.</Paso>
            <Paso n={2}>
              <span>Elige el archivo. Aquí el sistema solo lo guarda como respaldo — no lo abre ni lo procesa — así que acepta:</span>
              <ul className="list-disc space-y-1 pl-5 mt-1">
                <li><strong>PDF</strong> (.pdf)</li>
                <li><strong>Word</strong>, tanto el formato clásico <strong>Word 97-2003</strong> (.doc) como el moderno <strong>.docx</strong> — cualquiera de los dos funciona bien porque solo se guarda el archivo, no se lee su contenido.</li>
                <li><strong>Imágenes</strong>, cualquier formato (JPG, PNG, GIF, etc.).</li>
              </ul>
            </Paso>
            <Paso n={3}>Confirma la subida — <strong>una vez confirmado no se puede borrar ni editar</strong>, así que revisa el archivo antes de confirmar.</Paso>
          </ol>
          <Captura src="/docs/manual/modal-subir-documento.jpg" alt="Modal de subir documento" />
        </div>
      ),
    },
    {
      id: "subir-word-cliente",
      titulo: "Nota en Word para el reporte cliente",
      icon: FileText,
      keywords: "subir word docx reporte cliente nota word 97 mammoth convertir",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Es distinto al documento de soporte de arriba: aquí sí se <strong>lee el contenido</strong> del
            archivo para pasarlo, tal como está redactado, al reporte que recibe el cliente. Por eso el
            formato que aceptes importa.
          </p>
          <ol className="space-y-2">
            <Paso n={1}>En el detalle del pedido, junto a "PRUEBAS SOLICITADAS", presiona <strong>Subir Word</strong> (solo visible mientras el pedido no se haya cerrado ni tenga reportes generados).</Paso>
            <Paso n={2}>
              Selecciona el archivo. <strong>Solo se aceptan archivos .docx</strong> (Word 2007 en adelante) —
              el sistema no puede leer el formato antiguo <strong>Word 97-2003 (.doc)</strong> para esta
              función en particular.
            </Paso>
            <Paso n={3}>
              Si tu documento está en .doc, en Word ve a <strong>Archivo → Guardar como</strong> y elige{" "}
              <strong>Documento de Word (*.docx)</strong> antes de subirlo, o no se va a leer correctamente.
            </Paso>
            <Paso n={4}>Una vez subido, el botón cambia a <strong>Word cargado</strong>; usa la ✕ junto a él si necesitas quitarlo y subir otro.</Paso>
          </ol>
        </div>
      ),
    },
    {
      id: "revisar",
      titulo: "Revisar, aceptar o rechazar una prueba",
      icon: CheckCircle2,
      keywords: "revisar aceptar rechazar aprobar motivo coordinador gerente",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Aprobar o rechazar un resultado ya capturado es una acción de revisión — según la matriz de
            Permisos, la puede hacer un <strong>Coordinador</strong>, <strong>Gerente de Calidad</strong> o{" "}
            <strong>Administrador</strong>.
          </p>
          <ol className="space-y-2">
            <Paso n={1}>Abre la prueba con <strong>CONTINUAR</strong> para ver los datos ya capturados.</Paso>
            <Paso n={2}>
              Si todo está correcto, presiona <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" style={{ color: '#059669' }} />ACEPTAR</span>.
              Si algo está mal, presiona <span className="inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-destructive" />RECHAZAR</span> y escribe el motivo — quien capturó la prueba lo verá para corregirla.
            </Paso>
          </ol>
        </div>
      ),
    },
    {
      id: "cerrar-pedido",
      titulo: "Cerrar un pedido y generar reportes",
      icon: FileText,
      keywords: "cerrar pedido finalizar reportes bitácora reporte cliente acreditación autorizar informe jpg png firma sello tv-1960 q-1959 mm-1958",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Cerrar un pedido y autorizar su informe está reservado a{" "}
            <strong>Gerente de Calidad</strong> y <strong>Administrador</strong>.
          </p>
          <ol className="space-y-2">
            <Paso n={1}>El botón para finalizar/cerrar aparece solo cuando todas las pruebas del pedido ya fueron aceptadas o rechazadas.</Paso>
            <Paso n={2}>
              <span>Completa los datos que pide: contacto, muestra, quién autorizó, quién supervisó, y:</span>
              <ul className="list-disc space-y-1 pl-5 mt-1">
                <li><strong>Número de acreditación</strong> — elige entre <strong>TV-1960-015/25</strong>, <strong>Q-1959-302/25</strong> o <strong>MM-1958-253/25</strong>, según corresponda al tipo de prueba.</li>
                <li><strong>Imagen de firma/sello</strong> — <strong>solo JPG o PNG</strong>. Otros formatos (PDF, GIF, WEBP, HEIC, etc.) no se pueden subir aquí; conviértela a JPG o PNG antes de intentarlo de nuevo.</li>
              </ul>
            </Paso>
            <Paso n={3}>Al confirmar, el sistema genera la bitácora interna y el reporte para el cliente; ambos quedan disponibles para descargar desde el detalle del pedido.</Paso>
          </ol>
          <Captura src="/docs/manual/modal-cierre-pedido.jpg" alt="Modal de cierre de pedido" />
        </div>
      ),
    },
    {
      id: "reabrir-pedido",
      titulo: "Reabrir un pedido cerrado",
      icon: FileText,
      keywords: "reabrir pedido cerrado autorizar",
      contenido: (
        <p className="text-muted-foreground">
          Reabrir un pedido ya cerrado requiere autorización — solo quien tenga el permiso "Reabrir
          pedido" puede aprobar la solicitud. Si necesitas corregir algo en un pedido cerrado, pide a
          esa persona que habilite la reapertura.
        </p>
      ),
    },
    {
      id: "mcp",
      titulo: "Conectar Claude (asistente de IA) al sistema",
      icon: Bot,
      keywords: "mcp claude ia inteligencia artificial asistente conectar borrador permisos rol connector",
      contenido: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Dexterite se puede conectar a Claude (el asistente de IA) para que cualquier persona del
            equipo le pida en lenguaje normal cosas como "¿en qué estado está el pedido 4821?" o
            "precárgame la prueba NMX-A-109 del pedido 4821 con estos datos". Claude se conecta con{" "}
            <strong>tu propio usuario y contraseña de Dexterite</strong> — nunca con una cuenta
            genérica — así que ve exactamente lo mismo que tu rol te permite ver, y puede hacer
            exactamente lo mismo que tu rol te permite hacer.
          </p>

          <Alert className="flex gap-2 border-destructive/40 bg-destructive/5">
            <Lock className="h-4 w-4 shrink-0 text-destructive" />
            <AlertDescription>
              <strong>Esto aplica sin excepción, incluso para un Administrador:</strong> Claude{" "}
              <strong>nunca</strong> puede ACEPTAR ni RECHAZAR una prueba, <strong>nunca</strong> puede
              autorizar un informe, y <strong>nunca</strong> puede cerrar ni reabrir un pedido. Esas
              cuatro acciones solo existen dentro del sistema, hechas por una persona con el permiso
              correspondiente — no es una casilla que se pueda activar, esa función simplemente no
              existe del lado de Claude. Lo máximo que Claude puede hacer al escribir datos es dejar
              una prueba precargada en <Badge variant="outline">BORRADOR</Badge>, exactamente igual
              que cuando tú guardas un formulario sin terminarlo — alguien tiene que entrar al sistema
              a revisarla y aceptarla o rechazarla, como siempre.
            </AlertDescription>
          </Alert>

          <h4 className="font-medium">Cómo conectarlo (una vez, por persona)</h4>
          <ol className="space-y-2">
            <Paso n={1}>En Claude, ve a <strong>Settings → Connectors → Add custom connector</strong>.</Paso>
            <Paso n={2}>
              Ponle el nombre que quieras (p. ej. "Dexterite") y en URL pega{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">https://mcp-dexterite.oacsolutions.io/mcp</code>.
              Deja los campos de Client ID / Client Secret vacíos.
            </Paso>
            <Paso n={3}>
              Al conectar, se abre una pantalla de inicio de sesión propia de Dexterite (no es un
              formulario dentro del chat de Claude). Ahí metes tu correo y contraseña del sistema.
            </Paso>
            <Paso n={4}>Listo — desde ese momento puedes pedirle cosas a Claude sobre pedidos y pruebas en cualquier conversación.</Paso>
          </ol>

          <h4 className="font-medium">
            Qué puedes pedirle a Claude con tu rol{rol ? ` (${ROL_LABELS[rol] ?? rol})` : ''}
          </h4>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>Buscar pedidos por folio o cliente, y ver si están abiertos o cerrados. <Badge variant="outline" className="ml-1">Siempre disponible</Badge></li>
            <li>Ver el detalle de un pedido: qué pruebas tiene, cuáles están en borrador, aceptadas o rechazadas (y el motivo si fue rechazada). <Badge variant="outline" className="ml-1">Siempre disponible</Badge></li>
            <li>Ver las mediciones completas de una prueba específica. <Badge variant="outline" className="ml-1">Siempre disponible</Badge></li>
            <li>
              Ver los datos de contacto del cliente (correo, teléfono, dirección) al consultar un pedido.{" "}
              {puedeVerContactos
                ? <Badge style={{ backgroundColor: '#059669', color: '#fff' }}>Tu rol sí puede</Badge>
                : <Badge variant="destructive">Tu rol no puede</Badge>}
            </li>
            <li>
              Ver el link a la bitácora interna de un pedido.{" "}
              {puedeVerBitacoras
                ? <Badge style={{ backgroundColor: '#059669', color: '#fff' }}>Tu rol sí puede</Badge>
                : <Badge variant="destructive">Tu rol no puede</Badge>}
            </li>
            <li>
              Precargar una prueba nueva en borrador.{" "}
              {puedeCapturarResultado
                ? <Badge style={{ backgroundColor: '#059669', color: '#fff' }}>Tu rol sí puede</Badge>
                : <Badge variant="destructive">Tu rol no puede</Badge>}
            </li>
            <li>
              Editar una prueba que sigue en borrador (si ya fue aceptada o rechazada, Claude lo
              rechaza y te dice que hay que reabrirla desde el sistema).{" "}
              {puedeEditarBorrador
                ? <Badge style={{ backgroundColor: '#059669', color: '#fff' }}>Tu rol sí puede</Badge>
                : <Badge variant="destructive">Tu rol no puede</Badge>}
            </li>
          </ul>
          <p className="text-sm text-muted-foreground">
            Si le pides a Claude algo fuera de lo que tu rol permite, no falla en silencio: te explica
            qué permiso te falta, igual que si intentaras hacerlo dentro del sistema sin el botón
            correspondiente.
          </p>

          <h4 className="font-medium">Referencia completa por rol</h4>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Acción vía Claude</TableHead>
                  <TableHead className="text-center">Administrador</TableHead>
                  <TableHead className="text-center">Analista</TableHead>
                  <TableHead className="text-center">Coordinador</TableHead>
                  <TableHead className="text-center">Gerente de Calidad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Consultar pedidos y estado de pruebas</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Ver datos de contacto del cliente</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Ver link de la bitácora interna</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Precargar/editar una prueba en borrador</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">✅</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                </TableRow>
                <TableRow className="bg-destructive/5">
                  <TableCell>Aceptar / rechazar un resultado</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                </TableRow>
                <TableRow className="bg-destructive/5">
                  <TableCell>Autorizar un informe</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                </TableRow>
                <TableRow className="bg-destructive/5">
                  <TableCell>Cerrar o reabrir un pedido</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                  <TableCell className="text-center">❌</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            Las primeras cuatro filas reflejan la configuración por defecto de la sección{" "}
            <strong>Permisos</strong> — si un Administrador la cambia ahí, Claude respeta el cambio
            automáticamente la próxima vez que alguien se conecte. Las últimas tres filas están en
            rojo porque no dependen de ningún permiso: esas acciones no existen para Claude bajo
            ningún rol.
          </p>

          <h4 className="font-medium">Límites de uso (para que nadie sature el sistema)</h4>
          <p className="text-muted-foreground">
            Cada vez que Claude necesita buscar un pedido, revisar el estado de una prueba o
            precargar un borrador, hace por detrás <strong>una llamada</strong> al servidor del
            MCP — es distinto a "un mensaje de chat": una sola pregunta tuya puede disparar una o
            varias llamadas, dependiendo de cuánto necesite consultar Claude para responderte. Ese
            conteo de llamadas es lo que se limita, no cuánto le escribes a Claude.
          </p>
          <p className="text-muted-foreground">
            El límite es <strong>por persona</strong> (por tu correo, no por tu red/wifi) — que
            alguien más de la oficina esté usando Claude al mismo tiempo no afecta tu propia
            cuota:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li><strong>150 llamadas cada 10 minutos.</strong> Muy por encima de lo que genera una conversación normal — solo se siente si Claude está haciendo muchísimas consultas seguidas en poco tiempo.</li>
            <li><strong>1,500 llamadas por día.</strong> Un techo para que nadie pueda usar el MCP de forma intensiva y sostenida todo el día sin parar.</li>
            <li>Al iniciar sesión (meter tu contraseña), hay un límite aparte de <strong>10 intentos cada 15 minutos</strong> — es protección contra ataques de adivinar contraseñas, no debería notarse en el uso normal.</li>
          </ul>
          <Alert>
            <AlertDescription>
              Si llegas a un límite, Claude no falla en silencio: te lo dice directamente en su
              respuesta (por ejemplo "Estás haciendo demasiadas solicitudes seguidas — espera unos
              minutos"). No es un error del sistema ni perdiste nada — simplemente espera el
              tiempo indicado y vuelve a intentarlo.
            </AlertDescription>
          </Alert>
          <p className="text-xs text-muted-foreground">
            Estos límites existen para que una sola persona usando el MCP sin parar no sature el
            servidor ni dispare el costo de la base de datos para todo el equipo — no son un
            castigo, son una protección compartida.
          </p>
        </div>
      ),
    },
    {
      id: "tutoriales-video",
      titulo: "Tutoriales en video",
      icon: PlayCircle,
      keywords: "video tutorial gif crear guardar aceptar rechazar editar cerrar descargar reporte bitacora",
      contenido: (
        <div className="space-y-6">
          <p className="text-muted-foreground">
            Estas animaciones muestran cada pantalla paso a paso. Las que terminan en una acción que
            no se puede deshacer (Aceptar, Rechazar, Cerrar) se detienen justo antes del botón final
            de confirmación — no llegan a completarla, para no dejar un ejemplo real guardado en el
            sistema.
          </p>

          <div className="space-y-2">
            <h4 className="font-medium">1–2. Crear y guardar un reporte (llenar una prueba)</h4>
            <p className="text-sm text-muted-foreground">
              Primero se elige la prueba a agregar desde el catálogo del pedido.
            </p>
            <Captura src="/docs/manual/crear-reportes.gif" alt="Buscar y elegir una prueba para agregarla al pedido" />
            <p className="text-sm text-muted-foreground">
              Al llenarla y guardar los cambios, el formulario se ve así — el botón de guardado
              siempre está al final del formulario:
            </p>
            <Captura src="/docs/manual/editar-guardar-reportes.gif" alt="Formulario de una prueba en modo edición con el botón Guardar" nota="Este mismo formulario y botón son los que se usan para guardar una captura nueva." />
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">3. Aceptar un reporte</h4>
            <Captura src="/docs/manual/aceptar-reportes.gif" alt="Pantalla de revisión de una prueba con el botón Aceptar" nota="Se detiene antes de confirmar: al presionar Aceptar el sistema pide una confirmación final que ya no se puede deshacer." />
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">4. Rechazar un reporte</h4>
            <Captura src="/docs/manual/rechazar-reportes.gif" alt="Modal de razón de rechazo con un motivo de ejemplo escrito" nota="Se detiene antes de confirmar, por la misma razón que Aceptar." />
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">5. Editar un reporte</h4>
            <p className="text-sm text-muted-foreground">
              Es el mismo botón "Editar" del GIF de arriba (Crear/Guardar) — abre el formulario en modo
              edición con "Guardar Edición" al final.
            </p>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">6. Cerrar un pedido</h4>
            <Captura src="/docs/manual/cerrar-pedido.gif" alt="Formulario de datos para cierre de pedido siendo llenado" nota="Se detiene antes de Guardar: cerrar un pedido genera documentos reales y solo se deshace con el proceso formal de reapertura." />
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">7–8. Descargar el reporte y la bitácora</h4>
            <p className="text-sm text-muted-foreground">
              Ambos botones aparecen juntos en el detalle de un pedido ya cerrado. A diferencia de las
              anteriores, esta acción no cambia nada en el sistema — solo descarga el archivo — así que
              no hace falta detenerse antes de presionarlos.
            </p>
            <Captura src="/docs/manual/descargar-reporte-bitacora.jpg" alt="Botones Descargar Bitácora y Descargar Reporte Cliente" />
          </div>
        </div>
      ),
    },
    {
      id: "catalogo",
      titulo: "Catálogo de pruebas (Lista de Pruebas)",
      icon: TestTube,
      keywords: "catálogo lista de pruebas agregar editar tiempo estimado",
      contenido: (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            Aquí se ven todas las pruebas disponibles, con su descripción y tiempo estimado. Puedes
            buscarlas por nombre, descripción o tipo (Físico/Químico).
          </p>
          {puedeAdministrarCatalogo && (
            <p className="text-muted-foreground">
              Con tu rol puedes <strong>Agregar Prueba</strong> o <strong>Editar</strong> una existente
              desde esta misma pantalla.
            </p>
          )}
          <Captura src="/docs/manual/lista-de-pruebas.jpg" alt="Catálogo de pruebas disponibles" />
        </div>
      ),
    },
    ...(puedeVerContactos
      ? [{
          id: "clientes",
          titulo: "Clientes",
          icon: Building2,
          keywords: "clientes contactos correo teléfono",
          contenido: (
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Consulta los datos de contacto de cada cliente. Solo algunos roles pueden editarlos —
                revisa "Editar contactos" en Permisos si te falta el botón de Editar.
              </p>
              <Captura src="/docs/manual/clientes.jpg" alt="Lista de clientes" />
            </div>
          ),
        } as Seccion]
      : []),
    ...(esAdmin
      ? [{
          id: "usuarios",
          titulo: "Usuarios",
          icon: Users,
          keywords: "usuarios agregar editar rol área",
          adminOnly: true,
          contenido: (
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Solo un Administrador ve y gestiona esta sección. Cada tarjeta muestra el rol y área de
                la persona. Usa <strong>Agregar Usuario</strong> para dar de alta a alguien nuevo (su
                correo de Google debe coincidir exactamente) y <strong>Editar</strong> para cambiar su
                rol.
              </p>
              <Captura src="/docs/manual/usuarios.jpg" alt="Administración de usuarios" nota="Solo visible para Administradores." />
            </div>
          ),
        } as Seccion]
      : []),
    ...(esAdmin
      ? [{
          id: "permisos",
          titulo: "Permisos",
          icon: ShieldCheck,
          keywords: "permisos matriz roles acciones",
          adminOnly: true,
          contenido: (
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Define qué puede hacer cada rol (Analista, Coordinador, Gerente de Calidad) en cada
                categoría: Gestión de Resultados, Gestión de Informes y Pedidos, Bitácoras y Contactos,
                y Catálogos. El Administrador siempre tiene todo permitido. Los cambios se guardan
                automáticamente al activar o desactivar un interruptor.
              </p>
              <Captura src="/docs/manual/permisos.jpg" alt="Matriz de permisos por rol" nota="Solo visible para Administradores." />
            </div>
          ),
        } as Seccion]
      : []),
    {
      id: "faq",
      titulo: "Preguntas frecuentes",
      icon: HelpCircle,
      keywords: "problemas comunes no veo no puedo correo no autorizado ayuda soporte",
      contenido: (
        <div className="space-y-4">
          <div>
            <p className="font-medium">No veo un pedido que debería estar ahí.</p>
            <p className="text-muted-foreground">Revisa los filtros (estado y fecha de emisión) y el buscador — puede estar oculto por un filtro activo.</p>
          </div>
          <div>
            <p className="font-medium">Mi correo no está autorizado para entrar.</p>
            <p className="text-muted-foreground">Pide a un Administrador que te agregue en la sección Usuarios con el mismo correo de Google que usas para iniciar sesión.</p>
          </div>
          <div>
            <p className="font-medium">No me aparecen los botones de ACEPTAR / RECHAZAR ni el botón para cerrar el pedido.</p>
            <p className="text-muted-foreground">Esas acciones dependen de tu rol. Consulta la sección Permisos (o pide a un Administrador que lo revise) para confirmar qué tiene habilitado tu rol.</p>
          </div>
          <div>
            <p className="font-medium">Subí el documento equivocado.</p>
            <p className="text-muted-foreground">Los documentos adjuntos no se pueden borrar ni editar una vez confirmados. Avisa a un Administrador para que valore cómo proceder.</p>
          </div>
        </div>
      ),
    },
  ]

  const q = busqueda.trim().toLowerCase()
  const seccionesFiltradas = q
    ? secciones.filter(s => s.titulo.toLowerCase().includes(q) || s.keywords.includes(q))
    : secciones

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1>Documentación</h1>
          <p className="text-sm text-muted-foreground">Manual de uso del sistema, paso a paso.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4 mr-2" />
          Imprimir / Guardar PDF
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar en el manual..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="pl-9"
        />
      </div>

      {seccionesFiltradas.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          Sin resultados para "{busqueda}".
        </div>
      ) : (
        <Accordion type="multiple" className="rounded-lg border px-4">
          {seccionesFiltradas.map((seccion) => (
            <AccordionItem key={seccion.id} value={seccion.id}>
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <seccion.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  {seccion.titulo}
                  {seccion.adminOnly && (
                    <Badge variant="outline" className="text-[10px]">Solo Admin</Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>{seccion.contenido}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  )
}
